import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles, ThemeProvider, useTheme } from '@material-ui/core/styles';
import { Box, Grid, Paper, Typography, IconButton, Collapse } from '@material-ui/core';
import { ExpandLess, ExpandMore, LayersOutlined } from '@material-ui/icons';
import BackgroundMapPanel from '../../components/map/BackgroundMapPanel';
import OverlayPanelsToggle from '../../components/map/OverlayPanelsToggle';
import GoogleMap from '../../components/map/GoogleMap';
import ImageChooserCard from '../../components/map/ImageChooserCard';
import LoadedImagesAccordion from '../../components/map/LoadedImagesAccordion';
import { ShapeList } from '../../components';
import { useTranslation } from 'react-i18next';
import { useErrorNotice } from '../../../hooks/useErrorNotice';
import AttentionMessage from '../../components/core/AttentionMessage';
import ActivityIndicator from '../../components/core/ActivityIndicator';
import ShoreSAnalysisSteps from '../../components/visualization/shoreS/ShoreSAnalysisSteps';
import { useShoreSReferenceExtraction } from '../../components/visualization/shoreS/useShoreSReferenceExtraction';
import { useRoiOverlay } from '../../components/map/useRoiOverlay';
import { reprojectFeatureCollection } from '../../../algorithms/shoreS';
import { Actions as Map } from '../../../store/ducks/map';
import { addShape } from '../../../common/map';
import ReturnButtons from '../../components/core/ReturnButtons';
import {
  buildSessionExport,
  restorePendingBaseline,
  renderPendingReferenceLayer,
} from '../../../algorithms/shoreS/sessionExport';
import { exportSession, useLocalStorage } from '../../../common/utils';
import { exportGeoJson } from '../../../algorithms/shoreS/geoJsonExport';
import { useRegisterGeoJsonExport, useRegisterPdfExport, useRegisterSessionExport, useThemeMode } from '../../contexts/index.jsx';
import {
  buildMapStyles,
  createShorelineTheme,
  OVERLAY_COLUMN_MAX_HEIGHT,
  OVERLAY_COLUMN_MAX_HEIGHT_SAFE,
  OVERLAY_SIDE_GUTTER,
  OVERLAY_SCROLLBAR_GUTTER,
  OVERLAY_COLUMN_PADDING_TOP,
  OVERLAY_MAIN_COLUMN_WIDTH,
  OVERLAY_SIDE_COLUMN_WIDTH,
  OVERLAY_PANEL_WIDTH,

  OVERLAY_COLUMN_LAYOUT,
} from '../Shoreline/theme.js';
import { buildShoreSReport, REPORT_MODULE } from '../../../algorithms/shoreS/pdfReport';
import { captureImageForReport, renderReport, reportFileName } from '../../../common/pdfReport';
import { APP_ROOT_SELECTOR, settleMapForCapture } from '../../../common/captureElement';

const ROI_COLOR = '#7952B3';

const MODELED_COASTLINE_GROUP = 'shoreSModeledCoastlineData';

const MODELED_COLOR = '#1976d2';

// Backstop for `tilesloaded`, which never fires if the map can't reach its tiles.
const MAP_READY_FALLBACK_MS = 8000;

const useStyles = makeStyles((theme) => ({
  wrapper: {
    maxHeight: '100vh',
    flexGrow: 1,
    position: 'relative',
    overflow: 'hidden',
  },
  mapContainer: {
    display: 'flex',
    flexFlow: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    gap: 8,
    flexBasis: 'auto',
    flexGrow: 0,
    width: OVERLAY_MAIN_COLUMN_WIDTH,
    maxWidth: OVERLAY_MAIN_COLUMN_WIDTH,
    paddingTop: OVERLAY_COLUMN_PADDING_TOP,
    paddingLeft: OVERLAY_SIDE_GUTTER,
    paddingRight: OVERLAY_SCROLLBAR_GUTTER,
    overflowY: 'auto',
    maxHeight: OVERLAY_COLUMN_MAX_HEIGHT_SAFE,
    ...OVERLAY_COLUMN_LAYOUT,
  },
  sideColumn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: 8,
    flexBasis: 'auto',
    flexGrow: 0,
    width: OVERLAY_SIDE_COLUMN_WIDTH,
    maxWidth: OVERLAY_SIDE_COLUMN_WIDTH,
    marginLeft: 'auto',
    paddingTop: OVERLAY_COLUMN_PADDING_TOP,
    paddingLeft: OVERLAY_SCROLLBAR_GUTTER,
    paddingRight: OVERLAY_SIDE_GUTTER,
    overflowY: 'auto',
    maxHeight: OVERLAY_COLUMN_MAX_HEIGHT,
    ...OVERLAY_COLUMN_LAYOUT,
  },
  panelWrapper: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    flexShrink: 0,
    overflow: 'hidden',
  },
  panelHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(0.75, 1.25),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  mapOverlay: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: '100%',
    zIndex: 10,
    pointerEvents: 'none',
    '& > *': {
      pointerEvents: 'none',
      '& > *': {
        pointerEvents: 'auto',
      },
    },
  },
}));

/**
 * @function ProcessingShoreSPage
 * @description Shore-S's map/analysis page: shows the uploaded model's ROI and a preview of its
 * modeled coastlines on the map (until a validation run draws its own), and hosts
 * `ShoreSAnalysisSteps` (baseline/transects/analysis/export) alongside the image chooser and
 * loaded-images accordion. The reference-coastline extraction is kicked off here via
 * `useShoreSReferenceExtraction`, independently of the user's baseline/transect work but only
 * once the map has loaded, so the two don't compete and leave the user on a blank map.
 * @see {@link GoogleMap} - Google Map component
 * @see {@link ShoreSAnalysisSteps} - Shore-S analysis workflow component
 * @see {@link useShoreSReferenceExtraction} - Starts the reference extraction and exposes its status
 * @see {@link useRoiOverlay} - Draws the model's ROI and keeps the map centered on it
 * **Called by:
 * @see {MainPage} - via the `/processing_shore_s` Route.
 * @memberof module:ShoreS
 * @returns {JSX.Element} The Shore-S processing page.
 */
const ProcessingShoreSPage = () => {
  const dispatch = useDispatch();
  const satelliteIndex = useSelector((state) => state.acquisition.satelliteIndex);
  /**
   * The name of the KML the area came from, when it came from one. The export is named after it, so
   * a folder of saved analyses says which stretch of coast each one is.
   */
  const kmlFileName = useSelector((state) => state.acquisition.kmlFileName);
  const roiBBox = useSelector((state) => state.shoreSModel.roiBBox);
  const crs = useSelector((state) => state.shoreSModel.crs);
  const hasTemporalData = useSelector((state) => state.shoreSModel.hasTemporalData);
  const dateRange = useSelector((state) => state.shoreSModel.dateRange);
  const modelBBox = useSelector((state) => state.shoreSModel.modelBBox);
  const modeledCoastlinesGeoJSON = useSelector((state) => state.shoreSModel.modeledCoastlinesGeoJSON);
  const modeledCoastlinesDisplayGeoJSON = useSelector(
    (state) => state.shoreSModel.modeledCoastlinesDisplayGeoJSON,
  );
  const shoreSAnalysisState = useSelector((state) => state.shoreSAnalysis);
  const images = useSelector((state) => state.imagery.images);
  const shapesGroup = useSelector((state) => state.map.shapes);
  const classes = useStyles();
  const [t] = useTranslation();
  /**
   * Problems are reported in the corner instead of in a blocking dialog - see {@link useErrorNotice}.
   */
  const notifyError = useErrorNotice();
  const drawingManager = useRef();
  const mapReadyFallbackRef = useRef();
  const [baseLine, setBaseLine] = useState(null);
  const [mapReady, setMapReady] = useState(false);
  const [layersPanelOpen, setLayersPanelOpen] = useLocalStorage('shoreSLayersPanelOpen', true);
  const [mapType, setMapType] = useLocalStorage('shoreSMapType', 'roadmap');
  const [labelsVisible, setLabelsVisible] = useLocalStorage('shoreSMapLabels', true);
  const { shorelineThemeMode } = useThemeMode();
  const outerTheme = useTheme();
  const shoreSTheme = useMemo(
    () => createShorelineTheme(shorelineThemeMode, outerTheme),
    [outerTheme, shorelineThemeMode],
  );

  // Whichever of `tilesloaded` / the fallback timeout lost the race is dead weight once the map
  // is up, and the timer would fire into an unmounted component.
  useEffect(() => () => clearTimeout(mapReadyFallbackRef.current), []);

  const roiCoordinates = useMemo(() => {
    if (!roiBBox) return null;
    const [west, south, east, north] = roiBBox;
    return [
      [west, south],
      [east, south],
      [east, north],
      [west, north],
    ];
  }, [roiBBox]);

  const { mapRef, roiRef, onMapLoad, fitRoi } = useRoiOverlay(roiCoordinates, ROI_COLOR);

  const handleMapTypeChange = (type) => {
    setMapType(type);
    mapRef.current?.setMapTypeId(type);
  };

  useEffect(() => {
    if (!mapRef.current) return;
    mapRef.current.setOptions({ styles: buildMapStyles({ themeMode: shorelineThemeMode, mapType, labelsVisible }) });
  }, [shorelineThemeMode, mapType, labelsVisible, mapRef]);

  // Held back until the map has loaded: the extraction is heavy enough that starting both at
  // once leaves the map blank long enough to look like the page hung.
  const extraction = useShoreSReferenceExtraction(mapReady);


  /**
   * @function handleMapLoad
   * @description Forwards the loaded map to `useRoiOverlay` (which draws the ROI) and arms the
   * `mapReady` flag, the gate both the modeled-coastline layer and the reference extraction wait
   * on before starting.
   *
   * `GoogleMap`'s `onLoad` fires as soon as the `google.maps.Map` object is constructed, before a
   * single tile has painted, so it is far too early to gate on: the extraction would still start
   * over a blank map. `tilesloaded` is the first event that means the user can actually see
   * something. A timeout backs it up so a map that never finishes loading (offline, blocked host)
   * doesn't strand the page on the spinner forever.
   * @param {Object} map - The loaded google.maps.Map instance.
   * **Called by:
   * @see {GoogleMap} - via the onLoad prop.
   * @memberof module:ShoreS
   * @returns {void}
   */
  const handleMapLoad = (map) => {
    onMapLoad(map);
    map.setMapTypeId(mapType);

    window.google?.maps?.event?.addListenerOnce(map, 'tilesloaded', () => setMapReady(true));
    mapReadyFallbackRef.current = setTimeout(() => setMapReady(true), MAP_READY_FALLBACK_MS);

    const restoredBaseline = restorePendingBaseline(map);
    if (restoredBaseline) setBaseLine(restoredBaseline);
    renderPendingReferenceLayer(dispatch);
  };

  /**
   * @function exportSessionState
   * @description Downloads a JSON snapshot of the current model/satellite/selected-images and the
   * already-evaluated validation result, for Shore-S's "import session" icon to replay later — a
   * developer/testing shortcut so QA doesn't have to redo the whole analysis every time they need
   * a completed result to test against.
   * @see {@link buildSessionExport}
   * **Called by:
   * @see {NavigationBar} - via the standardized "export session for testing" icon's onClick.
   * @memberof module:ShoreS
   * @returns {void}
   */
  const exportSessionState = () => {
    exportSession(
      buildSessionExport({
        satelliteIndex,
        model: { crs, hasTemporalData, dateRange, roiBBox, modelBBox, modeledCoastlinesGeoJSON, modeledCoastlinesDisplayGeoJSON },
        analysis: shoreSAnalysisState,
        images,
      }),
      kmlFileName,
    );
  };
  useRegisterSessionExport('shore_s', shoreSAnalysisState?.errorMetrics ? exportSessionState : null);

  /**
   * @function exportGeoJsonLayers
   * @description Writes the baseline and both sets of coastlines - modelled and satellite-derived -
   * to a GeoJSON file for a GIS.
   * The three are held in different CRSs: the baseline is drawn on the map and so is WGS84 already,
   * the modelled coastlines are handed over in the reprojected form the map draws, and the reference
   * ones are in the model's working CRS, which is why it goes along with them.
   * @memberof module:ShoreS
   * @returns {void}
   */
  const exportGeoJsonLayers = () => {
    if (!exportGeoJson({
      baselineCoords: shoreSAnalysisState?.baselineCoords,
      modeledCoastlines: modeledCoastlinesWGS84,
      referenceCoastlines: shoreSAnalysisState?.referenceCoastlinesGeoJSON,
      crs,
    })) notifyError(t('forms.acquisition.geoJsonExport.empty'));
  };
  useRegisterGeoJsonExport('shore_s', shoreSAnalysisState?.errorMetrics ? exportGeoJsonLayers : null);

  /**
   * @function reportText
   * @description Every piece of wording the report needs, translated here and handed to the
   * builder, which stays a pure function of the analysis - see {@link buildShoreSReport}.
   * @memberof module:Core
   * @returns {Object}
   */
  const reportText = () => {
    const key = (name) => `forms.core.pdfReport.${name}`;
    const own = (name) => `forms.core.pdfReport.shore_s.${name}`;
    const group = (name) => t(own(name), { returnObjects: true, defaultValue: t(key(name), { returnObjects: true }) });
    return {
      ...t(key('common'), { returnObjects: true }),
      ...t(own('text'), { returnObjects: true }),
      subtitle: t(key('subtitle'), { date: new Date().toLocaleString() }),
      missingImage: (caption) => t(key('missingImage'), { caption }),
      screenCaption: t(key('screenCaption')),
      rates: group('rates'),
      classes: group('classes'),
      transects: group('transects'),
      transectNote: {
        complete: (values) => t(key('transectComplete'), values),
        truncated: (values) => t(key('transectTruncated'), values),
      },
      transectComplete: (values) => t(key('transectComplete'), values),
      transectTruncated: (values) => t(key('transectTruncated'), values),
      forecastYears: (values) => t(key('forecastYears'), values),
      missingTideNote: (values) => t(own('missingTideNote'), values),
      tide: {
        range: (values) => t(own('tideRangeValue'), values),
        span: (values) => t(own('tideSpanValue'), values),
      },
      errors: group('errors'),
      byDate: group('byDate'),
      byTransect: group('byTransect'),
      bays: group('bays'),
      controlPoints: group('controlPoints'),
      waterlines: group('waterlines'),
      cover: group('cover'),
    };
  };

  /**
   * @function exportPdfReport
   * @description Writes the whole analysis to one PDF.
   * The map is captured from the screen rather than fetched, because what this module draws is a
   * Google Maps overlay and not an image with a URL - see {@link captureImageForReport}.
   * @memberof module:Core
   * @returns {Promise<void>}
   */
  const exportPdfReport = async () => {
    try {
      const text = reportText();
      /**
       * The map is captured from the screen, so it is framed on the analysis first - otherwise the
       * report shows wherever the user had last panned to. See {@link settleMapForCapture}, which
       * also waits for the tiles of the new view to arrive.
       */
      await settleMapForCapture(mapRef.current, fitRoi);
      const screen = await captureImageForReport(APP_ROOT_SELECTOR, text.screenCaption);
      const report = buildShoreSReport({
        baselineCoords: shoreSAnalysisState?.baselineCoords,
        crs,
        analysis: shoreSAnalysisState,
        modeledCoastlines: modeledCoastlinesWGS84,
        referenceCoastlines: shoreSAnalysisState?.referenceCoastlinesGeoJSON,
        images: [screen],
        text,
      });
      renderReport(report).save(reportFileName(REPORT_MODULE));
    } catch (error) {
      console.error('exportPdfReport error:', error);
      notifyError(t('forms.core.pdfReport.failed'));
    }
  };
  useRegisterPdfExport(REPORT_MODULE, shoreSAnalysisState?.errorMetrics ? exportPdfReport : null);


  const modeledCoastlinesWGS84 = useMemo(() => {
    const featureCollection = modeledCoastlinesDisplayGeoJSON ?? modeledCoastlinesGeoJSON;
    if (!crs || !featureCollection?.features?.length) return null;
    try {
      return reprojectFeatureCollection(featureCollection, crs, 'EPSG:4326');
    } catch (e) {
      console.error('[ShoreS] failed to reproject modeled coastlines for display', e);
      return null;
    }
  }, [crs, modeledCoastlinesDisplayGeoJSON, modeledCoastlinesGeoJSON]);


  useEffect(() => {
    if (!mapReady || !modeledCoastlinesWGS84?.features?.length) return undefined;

    const shapes = modeledCoastlinesWGS84.features
      .map((feature) => addShape(feature, MODELED_COLOR, 1, MODELED_COASTLINE_GROUP))
      .filter(Boolean);
    if (!shapes.length) return undefined;

    dispatch(
      Map.addShapes(
        shapes,
        'forms.shoreS.map.legendModeled',
        MODELED_COASTLINE_GROUP,
        modeledCoastlinesWGS84.features,
      ),
    );

    return () => dispatch(Map.removeShapeGroup(MODELED_COASTLINE_GROUP));
  }, [mapReady, modeledCoastlinesWGS84, dispatch]);

  return (
    <ThemeProvider theme={shoreSTheme}>
    <Box className={classes.wrapper}>
      <Grid
        container
        justifyContent="space-between"
        alignItems="flex-start"
        spacing={0}
        className={classes.mapOverlay}
      >
        <Grid item xs={9} className={classes.mapContainer}>
          <OverlayPanelsToggle side="left" />
          {roiBBox ? (
            <>
              <Paper className={classes.panelWrapper} elevation={1}>
                <Box className={classes.panelHeader}>
                  <Box display="flex" alignItems="center" gridGap={6}>
                    <LayersOutlined fontSize="small" color="primary" />
                    <Typography variant="h6">{t('forms.shorelineAnalysis.layersPanelTitle')}</Typography>
                  </Box>
                  <IconButton size="small" onClick={() => setLayersPanelOpen((prev) => !prev)}>
                    {layersPanelOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                  </IconButton>
                </Box>
                <Collapse in={layersPanelOpen}>
                  <ShapeList
                    embedded
                    shapesGroup={shapesGroup}
                    baseLine={baseLine}
                    roiCoords={roiRef}
                    drawingManager={drawingManager}
                    transectLegendVariant="shoreSBias"
                  />
                </Collapse>
              </Paper>

              <ShoreSAnalysisSteps
                t={t}
                mapRef={mapRef}
                roiCoords={roiRef}
                baseLine={baseLine}
                setBaseLine={setBaseLine}
                drawingManager={drawingManager}
                extraction={extraction}
              />
            </>
          ) : (
            <AttentionMessage
              title={t('forms.shoreS.map.noModelTitle')}
              message={t('forms.shoreS.map.noModelMessage')}
            />
          )}
        </Grid>

        <Grid item xs={3} className={classes.sideColumn}>
          <OverlayPanelsToggle side="right" />
          <BackgroundMapPanel
            mapType={mapType}
            onMapTypeChange={handleMapTypeChange}
            labelsVisible={labelsVisible}
            onLabelsVisibleChange={setLabelsVisible}
            t={t}
          />
          <LoadedImagesAccordion />
          <ImageChooserCard shoreSMode vertical="shore-s" />
        </Grid>
      </Grid>
      <ReturnButtons
        lastStepPath="/main/shore_s/4"
        onBeforeReturnToBegin={() => sessionStorage.removeItem('shoreSSelectedDates')}
      />
      {!mapReady && <ActivityIndicator forceShow title={t('forms.core.loading.map')} />}
      <GoogleMap onLoad={handleMapLoad} style={{ position: 'absolute' }} mapOptions={{ mapTypeControl: false }} />
    </Box>
    </ThemeProvider>
  );
};

export default ProcessingShoreSPage;
